import S from 's-js';
import * as Promise from 'bluebird';

/**
 * Debounce the given function, returning a Promise which resolves with the value returned 
 * by the function when it finally runs. 
 * Multiple calls before the function runs return the same Promise.
 * The current Promise (whether resolved or pending) is available in the .current() signal.
 * Immediate execution can be forced by calling .flush().
 * 
 * __ Example timeline, with `wait` of 5 dots __
 * 
 * calls    :      c c c c               c c           c
 * waiting  :       . . . .....           . .....       ..
 * fn execs :                  x                 x        x
 * current(): |---||-----------=========||-------=====||--=======
 * flush()  :                      f                      f
 * 
 * Notes:
 * - lifespans of Promises demonstrated by spans |---| in `current()`
 * - in those spans, '=' means Promise is resolved, b/c `fn` has run
 * - 1st `flush()` is a no-op since Promise is already resolved
 * - 2nd `flush()` forces early run of `fn`
 * 
 * Be aware that if your `fn` returns a Promise and you do `debounced.current().then(() => ...)`, 
 * your code will actually wait not just until `fn` runs but until the Promise it returns resolves,
 * due to the fact that resolving a Promise with a another Promise waits on the second Promise too.
 * 
 * @param fn the function to debounce
 * @param wait how much quiet to wait for before running `fn`
 * @param init the value of the initial promise, before `fn` has been run
 */
export function debouncePromise<Args extends any[], Result>(fn : (...args : Args) => Result | PromiseLike<Result>, wait : number, init : Result) {
    let timeoutId = 0, // current setTimeout() id, or 0 if none active
        lastArgs = null! as Args, // last args passsed in, cached to use when we call `fn`
        promise = Promise.resolve(init), // current promise, plus its resolve/reject fns
        resolve = null! as (result : Result | PromiseLike<Result>) => void,
        reject = null! as (err : any) => void;

    const 
        current = S.data(promise),
        startNewPromise = () => current(promise = new Promise<Result>((_resolve, _reject) => {
            resolve = _resolve;
            reject = _reject;
        })),
        debounced = (...args : Args) => {
            if (timeoutId !== 0) {
                clearTimeout(timeoutId);
            } else {
                startNewPromise();
            }
            lastArgs = args;
            timeoutId = setTimeout(exec, wait);
            return promise;
        },
        exec = () => {
            const _resolve = resolve, _reject = reject;
            timeoutId = 0;
            try {
                _resolve(fn(...lastArgs));
            } catch (ex) {
                _reject(ex);
            }
        },
        flush = () => {
            const _promise = promise;
            if (timeoutId !== 0) {
                clearTimeout(timeoutId);
                exec();
            }
            return _promise;
        }

    debounced.current = () => current();
    debounced.flush = flush;

    return debounced;
};


// WEBPACK FOOTER //
// ./lib/debouncePromise.ts